/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { OrdenProduccion, User } from '../types';
import { formatColombiaDateTime } from '../utils/colombiaTime';
import { Plus, Trash2, CheckCircle, HelpCircle, FileText, Calendar, Shield, Sparkles, FileSpreadsheet, Download, Pencil, Search, X, AlertTriangle } from 'lucide-react';
import { motion } from 'motion/react';
// @ts-ignore
import insumedicLogo from '../assets/images/insumedic_logo_v2_1779469546019.png';

interface RegistroOrdenesProps {
  ordenes: OrdenProduccion[];
  users: User[];
  currentUser: User;
  onAddOrden: (nuevaOrden: OrdenProduccion) => void;
  onDeleteOrden: (id: string) => void;
  onDeleteMultipleOrdenes?: (ids: string[]) => void;
  onClearAllData?: () => void;
  onUpdateOrdenStatus?: (
    id: string, 
    newStatus: 'Pendiente por Aceptar' | 'Aceptada' | 'Cerrada' | 'Cancelada',
    extraFields?: Partial<OrdenProduccion>
  ) => void;
  isReadOnly?: boolean;
}

// Popular meds templates to facilitate testing
const MED_TEMPLATES = [
  {
    codigoMedicamento: 'MED-102',
    nombreGenerico: 'Amoxicilina',
    nombreComercial: 'Amoxil',
    concentracion: '500 mg',
    formaFarmaceutica: 'Cápsula',
    laboratorioFabricante: 'GlaxoSmithKline',
    registroSanitario: 'INVIMA 2020M-002139',
    cum: '19981212-3',
    manejoEspecial: 'NO' as const,
  },
  {
    codigoMedicamento: 'MED-334',
    nombreGenerico: 'Tramadol Clorhidrato',
    nombreComercial: 'Tramal',
    concentracion: '50 mg / mL',
    formaFarmaceutica: 'Solución Inyectable',
    laboratorioFabricante: 'Grunenthal S.A.S.',
    registroSanitario: 'INVIMA 2018M-000412',
    cum: '19904212-1',
    manejoEspecial: 'SI' as const,
  },
  {
    codigoMedicamento: 'MED-550',
    nombreGenerico: 'Metformina Clorhidrato',
    nombreComercial: 'Glucophage',
    concentracion: '850 mg',
    formaFarmaceutica: 'Tableta',
    laboratorioFabricante: 'Merck S.A.S.',
    registroSanitario: 'INVIMA 2019M-001223-R2',
    cum: '19951234-9',
    manejoEspecial: 'NO' as const,
  }
];

export default function RegistroOrdenes({
  ordenes,
  users,
  currentUser,
  onAddOrden,
  onDeleteOrden,
  onDeleteMultipleOrdenes,
  onClearAllData,
  onUpdateOrdenStatus,
  isReadOnly = false,
}: RegistroOrdenesProps) {
  // Selection and deletion states
  const [selectedOrdenIds, setSelectedOrdenIds] = useState<string[]>([]);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);

  // Fast Search State
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOrdenes = ordenes.filter((ord) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    const consecutivo = ord.consecutivoIngreso !== undefined ? String(ord.consecutivoIngreso) : '';
    return (
      ord.numeroOrden.toLowerCase().includes(term) ||
      ord.nombreGenerico.toLowerCase().includes(term) ||
      ord.nombreComercial.toLowerCase().includes(term) ||
      ord.loteRP.toLowerCase().includes(term) ||
      ord.loteFabricante.toLowerCase().includes(term) ||
      ord.codigoMedicamento.toLowerCase().includes(term) ||
      ord.laboratorioFabricante.toLowerCase().includes(term) ||
      ord.elaboradoPor.toLowerCase().includes(term) ||
      consecutivo.includes(term)
    );
  });

  // Editing State
  const [editingOrdenId, setEditingOrdenId] = useState<string | null>(null);

  const handleStartEdit = (ord: OrdenProduccion) => {
    setEditingOrdenId(ord.id);
    setNumeroOrden(ord.numeroOrden);
    setLoteRP(ord.loteRP);
    setLoteFabricante(ord.loteFabricante);
    setCodigoMedicamento(ord.codigoMedicamento);
    setNombreGenerico(ord.nombreGenerico);
    setNombreComercial(ord.nombreComercial);
    setConcentracion(ord.concentracion);
    setFormaFarmaceutica(ord.formaFarmaceutica);
    setLaboratorioFabricante(ord.laboratorioFabricante);
    setFechaVencimiento(ord.fechaVencimiento);
    setRegistroSanitario(ord.registroSanitario);
    setCum(ord.cum);
    setTamanoLote(ord.tamanoLote.toString());
    setManejoEspecial(ord.manejoEspecial);
    setEstadoOrden(ord.estadoOrden);
    setElaboradoPor(ord.elaboradoPor || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingOrdenId(null);
    setNumeroOrden('');
    setLoteRP('');
    setLoteFabricante('');
    setCodigoMedicamento('');
    setNombreGenerico('');
    setNombreComercial('');
    setConcentracion('');
    setFormaFarmaceutica('');
    setLaboratorioFabricante('');
    setFechaVencimiento('');
    setRegistroSanitario('');
    setCum('');
    setTamanoLote('');
    setManejoEspecial('NO');
    setEstadoOrden('Pendiente por Aceptar');
    setElaboradoPor(currentUser.nombreCompleto);
  };

  // Form State
  const [numeroOrden, setNumeroOrden] = useState('');
  const [consecutivoIngreso, setConsecutivoIngreso] = useState<number>(1);
  const [loteRP, setLoteRP] = useState('');
  const [loteFabricante, setLoteFabricante] = useState('');
  const [codigoMedicamento, setCodigoMedicamento] = useState('');
  const [nombreGenerico, setNombreGenerico] = useState('');
  const [nombreComercial, setNombreComercial] = useState('');
  const [concentracion, setConcentracion] = useState('');
  const [formaFarmaceutica, setFormaFarmaceutica] = useState('');
  const [laboratorioFabricante, setLaboratorioFabricante] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [registroSanitario, setRegistroSanitario] = useState('');
  const [cum, setCum] = useState('');
  const [tamanoLote, setTamanoLote] = useState('');
  const [manejoEspecial, setManejoEspecial] = useState<'SI' | 'NO' | 'FOTOSENSIBLE'>('NO');
  const [estadoOrden, setEstadoOrden] = useState<'Pendiente por Aceptar' | 'Aceptada' | 'Cerrada' | 'Cancelada'>('Pendiente por Aceptar');
  const [elaboradoPor, setElaboradoPor] = useState(currentUser.nombreCompleto);

  // Sync elaboradoPor with currentUser when not editing an existing order
  React.useEffect(() => {
    if (!editingOrdenId) {
      setElaboradoPor(currentUser.nombreCompleto);
    }
  }, [currentUser.nombreCompleto, editingOrdenId]);

  // Helper function to get date formatted as DDMMAAAA in Colombia Time Zone (GMT-5)
  const getDisposicionDateString = (): string => {
    const parts = new Intl.DateTimeFormat('es-CO', {
      timeZone: 'America/Bogota',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).formatToParts(new Date());
    
    const dd = parts.find(p => p.type === 'day')?.value || '01';
    const mm = parts.find(p => p.type === 'month')?.value || '01';
    const aaaa = parts.find(p => p.type === 'year')?.value || '2026';
    return `${dd}${mm}${aaaa}`;
  };

  // Helper function to check for duplicate order numbers
  const isNumeroOrdenDuplicado = (num: string): boolean => {
    if (!num || !num.trim()) return false;
    const cleaned = num.trim().toLowerCase();
    return ordenes.some(
      (o) => o.id !== editingOrdenId && o.numeroOrden.trim().toLowerCase() === cleaned
    );
  };

  // Info alerts
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [lastCreatedOrden, setLastCreatedOrden] = useState<OrdenProduccion | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Helper functions for selection
  const handleToggleSelect = (id: string) => {
    setSelectedOrdenIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const visibleIds = ordenes.map(o => o.id);
    const allVisibleSelected = visibleIds.every(id => selectedOrdenIds.includes(id));
    
    if (allVisibleSelected) {
      // Deselect all
      setSelectedOrdenIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      // Select all
      setSelectedOrdenIds(prev => {
        const combined = [...prev, ...visibleIds];
        return Array.from(new Set(combined));
      });
    }
  };

  // Helper function to calculate the highest authoritative consecutive number in the tenant
  const getNextAuthoritativeConsecutive = (): number => {
    let maxConsecutive = 0;
    for (const ord of ordenes) {
      if (typeof ord.consecutivoIngreso === 'number' && ord.consecutivoIngreso > maxConsecutive) {
        maxConsecutive = ord.consecutivoIngreso;
      }
      // Also check if numeroOrden contains -<number> suffix as fallback
      if (ord.numeroOrden) {
        const parts = ord.numeroOrden.split('-');
        if (parts.length >= 3) {
          const lastNum = parseInt(parts[parts.length - 1], 10);
          if (!isNaN(lastNum) && lastNum > maxConsecutive) {
            maxConsecutive = lastNum;
          }
        }
      }
    }
    return maxConsecutive + 1;
  };

  // Pre-populate OP code following OP-DDMMAAAA-CONSECUTIVO backed by tenant state
  React.useEffect(() => {
    if (!editingOrdenId) {
      let consecutive = getNextAuthoritativeConsecutive();
      const dStr = getDisposicionDateString();
      
      // Ensure the auto-generated order number is strictly unique against all existing tenant orders
      let defaultOp = `OP-${dStr}-${consecutive}`;
      while (ordenes.some(o => o.numeroOrden.trim().toLowerCase() === defaultOp.toLowerCase())) {
        consecutive += 1;
        defaultOp = `OP-${dStr}-${consecutive}`;
      }

      setConsecutivoIngreso(consecutive);
      setNumeroOrden(defaultOp);
      setLoteRP(`RP-${consecutive}`);
    }
  }, [ordenes, editingOrdenId]);

  const handleGeneratePDF = async (ord: OrdenProduccion) => {
    const loadLogo = () => {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.src = insumedicLogo;
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load logo'));
      });
    };

    let logoImg: HTMLImageElement | null = null;
    try {
      logoImg = await loadLogo();
    } catch (err) {
      console.error("Error cargando el logo de INSUMEDIC:", err);
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const margin = 15;
    let y = 15;

    // Header banner with deep slate/blue color hex #004b87 (Corporate pharmaceutical look)
    doc.setFillColor(0, 75, 135); // #004b87
    doc.rect(margin, y, 180, 26, 'F');

    // Draw white container on top-left of header and display attached INSUMEDIC logo
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin + 2, y + 2, 35, 22, 1.5, 1.5, 'F');
    if (logoImg) {
      try {
        doc.addImage(logoImg, 'PNG', margin + 3, y + 3, 33, 20);
      } catch (err) {
        console.error("Error setting logo image in PDF:", err);
      }
    }

    // Title / Corporate Text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('COMERCIALIZADORA INSUMEDIC S.A.S. ZOMAC - NIT 901201223', margin + 40, y + 8);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text('ORDEN DE PRODUCCIÓN Y REGISTRO DE PRODUCTO', margin + 40, y + 13.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(191, 219, 254);
    doc.text(`Consecutivo: OP-${ord.consecutivoIngreso || 'N/A'} | Fecha de Registro: ${new Date(ord.fechaRegistro).toLocaleDateString('es-CO')} | Documento Oficial`, margin + 40, y + 19);

    y += 34;

    // Grid details table design
    doc.setTextColor(15, 23, 42); // slate-900
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text('INFORMACIÓN DE LA ORDEN DE PRODUCCIÓN', margin, y);
    
    y += 4;
    doc.setDrawColor(148, 163, 184); // slate-400
    doc.setLineWidth(0.35);
    doc.line(margin, y, margin + 180, y);

    y += 5;

    // Draw fields in 2 columns
    const drawRow = (label1: string, val1: string, label2: string, val2: string) => {
      const h = 8.5;
      const topY = y - 4.5;
      const botY = topY + h;

      // Draw background for label cells
      doc.setFillColor(248, 250, 252); // slate-50
      doc.rect(margin, topY, 40, h, 'F');
      doc.rect(margin + 90, topY, 40, h, 'F');

      // Draw background for value cells
      doc.setFillColor(255, 255, 255); // white
      doc.rect(margin + 40, topY, 50, h, 'F');
      doc.rect(margin + 130, topY, 50, h, 'F');

      // Draw all border lines for this row to separate each field
      doc.setDrawColor(148, 163, 184); // slate-400
      doc.setLineWidth(0.25);
      
      // Horizontal borders
      doc.line(margin, topY, margin + 180, topY);
      doc.line(margin, botY, margin + 180, botY);

      // Vertical borders separating each field
      doc.line(margin, topY, margin, botY); // leftmost border
      doc.line(margin + 40, topY, margin + 40, botY); // separating label1 / val1
      doc.line(margin + 90, topY, margin + 90, botY); // middle separator
      doc.line(margin + 130, topY, margin + 130, botY); // separating label2 / val2
      doc.line(margin + 180, topY, margin + 180, botY); // rightmost border

      // Text placements inside the cells
      doc.setTextColor(71, 85, 105); // slate-600
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text(label1, margin + 2.5, y);

      doc.setTextColor(15, 23, 42); // slate-900
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      
      // Clip text to fit cells if necessary
      const maxCharsVal1 = 28;
      const cleanVal1 = val1 && val1.length > maxCharsVal1 ? val1.substring(0, maxCharsVal1 - 3) + '...' : (val1 || 'N/A');
      doc.text(cleanVal1, margin + 42.5, y);

      doc.setTextColor(71, 85, 105); // slate-600
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text(label2, margin + 92.5, y);

      doc.setTextColor(15, 23, 42); // slate-900
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);

      const maxCharsVal2 = 28;
      const cleanVal2 = val2 && val2.length > maxCharsVal2 ? val2.substring(0, maxCharsVal2 - 3) + '...' : (val2 || 'N/A');
      doc.text(cleanVal2, margin + 132.5, y);

      y += h;
    };

    const findUserRobustly = (nombre: string | undefined) => {
      if (!nombre) return undefined;
      return (users || []).find(
        (u) => (u.nombreCompleto || '').trim().toLowerCase() === nombre.trim().toLowerCase()
      );
    };

    const elaboradoUserObj = findUserRobustly(ord.elaboradoPor);
    const verificadoUserObj = findUserRobustly(ord.verificadoPor || 'Director Técnico / Calidad');

    // Find a user with cargo 'Auxiliar de Reempaque' as requested by the user, otherwise fallback to the order creator
    const elabUser = (users || []).find(u => u.cargo === 'Auxiliar de Reempaque') || elaboradoUserObj;
    const elabName = elabUser?.nombreCompleto || ord.elaboradoPor || 'LAURA PATRICIA CARRERO PERDOMO';

    drawRow('Consecutivo de Ingreso:', String(ord.consecutivoIngreso || 'N/A'), 'Número de Orden:', ord.numeroOrden);
    drawRow('Lote de Reempaque (LRP):', ord.loteRP, 'Lote de Fabricante:', ord.loteFabricante);
    drawRow('Código Medicamento:', ord.codigoMedicamento, 'Nombre Genérico:', ord.nombreGenerico);
    drawRow('Nombre Comercial:', ord.nombreComercial, 'Concentración:', ord.concentracion);
    drawRow('Forma Farmacéutica:', ord.formaFarmaceutica, 'Laboratorio Fabricante:', ord.laboratorioFabricante);
    drawRow('Fecha Vencimiento:', ord.fechaVencimiento, 'Registro Sanitario:', ord.registroSanitario);
    drawRow('CUMS:', ord.cum, 'Tamaño del Lote:', `${ord.tamanoLote.toLocaleString()} unidades`);
    let manejoEspecialText = 'NO (Manejo estándar)';
    if (ord.manejoEspecial === 'SI') {
      manejoEspecialText = 'SÍ (Manejo de cadena o especial)';
    } else if (ord.manejoEspecial === 'FOTOSENSIBLE') {
      manejoEspecialText = 'SI (Medicamento Fotosensible)';
    }
    drawRow('Manejo Especial:', manejoEspecialText, 'Estado de la Orden:', ord.estadoOrden || 'Pendiente por Aceptar');
    drawRow('Elaborado Por:', elabName, 'Verificado Por:', ord.verificadoPor || 'Pendiente de Control');

    y += 10;

    // Legal and technical verification disclaimer
    doc.setFillColor(239, 246, 255); // blue-50
    doc.setDrawColor(191, 219, 254); // blue-200
    doc.rect(margin, y, 180, 22, 'FD');

    doc.setTextColor(30, 58, 138); // blue-900
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('CONTROL TÉCNICO Y ASEGURAMIENTO DE CALIDAD', margin + 4, y + 5);

    doc.setTextColor(59, 130, 246); // blue-500
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text('• Este documento es soporte oficial e inalterable del proceso de reempaque y fraccionamiento de INSUMEDIC S.A.S.', margin + 4, y + 10);
    doc.text('• Los medicamentos deben cumplir con la etiqueta de rotulado aprobada y la inspección técnica de empaque individual.', margin + 4, y + 14);
    doc.text('• Toda desviación de rendimiento o especificación en el fraccionamiento debe ser reportada antes del dictamen final.', margin + 4, y + 18);

    y += 35;

    // Signature Area
    const getSignatureImageWithFallback = (userObj: User | null | undefined): string | null => {
      if (!userObj) return null;
      if (userObj.firma) return userObj.firma;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 100;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, 300, 100);
          ctx.strokeStyle = '#1d4ed8'; // blue-700
          ctx.lineWidth = 1.8;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          let hash = 0;
          const name = userObj.nombreCompleto;
          for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
          }
          const absHash = Math.abs(hash);

          ctx.beginPath();
          let x = 40;
          const yCoord = 50;
          ctx.moveTo(x, yCoord);
          
          const loops = Math.min(6, Math.max(3, name.split(' ')[0].length));
          for (let j = 0; j < loops; j++) {
            const nextX = x + 12 + (absHash % (j + 4));
            const topCurve = yCoord - 12 - (absHash % 8);
            const bottomCurve = yCoord + 12 + (absHash % 6);
            ctx.bezierCurveTo(x + 4, topCurve, nextX - 4, bottomCurve, nextX, yCoord - (absHash % 4));
            x = nextX;
          }

          ctx.bezierCurveTo(x + 20, yCoord + 10, x + 30, yCoord - 20, x + 40, yCoord);
          ctx.stroke();

          ctx.beginPath();
          ctx.lineWidth = 1.3;
          ctx.moveTo(35, yCoord + 10 + (absHash % 5));
          ctx.bezierCurveTo(100, yCoord + 24, x, yCoord + 5, x + 35, yCoord + 14);
          ctx.stroke();

          return canvas.toDataURL('image/png');
        }
      } catch (e) {
        console.error("Error generating fallback signature:", e);
      }
      return null;
    };

    const elabSignature = getSignatureImageWithFallback(elabUser);
    const verifSignature = getSignatureImageWithFallback(verificadoUserObj);

    if (elabSignature) {
      try {
        doc.addImage(elabSignature, 'PNG', margin + 10, y - 18, 45, 14);
      } catch (err) {
        console.error("Error setting elaborado signature in PDF:", err);
      }
    }

    if (verifSignature) {
      try {
        doc.addImage(verifSignature, 'PNG', margin + 110, y - 18, 45, 14);
      } catch (err) {
        console.error("Error setting verificado signature in PDF:", err);
      }
    }

    doc.setDrawColor(203, 213, 225); // slate-300
    doc.line(margin + 5, y, margin + 70, y);
    doc.line(margin + 105, y, margin + 170, y);

    doc.setTextColor(15, 23, 42); // slate-900
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text('Firma y Sello de Elaborado', margin + 15, y + 4.5);
    doc.setTextColor(71, 85, 105); // slate-600
    doc.text(elabName, margin + 15, y + 8.5);
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139); // slate-500
    doc.text('Auxiliar de Reempaque', margin + 15, y + 12);

    doc.setFontSize(7.5);
    doc.setTextColor(15, 23, 42); // slate-900
    doc.text('Firma y Sello de Verificación', margin + 115, y + 4.5);
    doc.setTextColor(71, 85, 105); // slate-600
    doc.text(ord.verificadoPor || 'Director Técnico / Calidad', margin + 115, y + 8.5);
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139); // slate-500
    doc.text(verificadoUserObj?.cargo || 'Director Técnico y de Calidad', margin + 115, y + 12);

    // Footer
    y = 282;
    doc.setDrawColor(226, 232, 240); // slate-200
    doc.line(margin, y, margin + 180, y);
    doc.setTextColor(148, 163, 184); // slate-400
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text('Soporte de Calidad Farmacéutica - Comercializadora Insumedic S.A.S. Zomac NIT: 901201223 - Autogenerado electrónicamente.', margin, y + 4);

    doc.save(`INSUMEDIC_Orden_Produccion_${ord.numeroOrden}.pdf`);
  };

  // Auto-generate some IDs for easy entry
  const handleAutoGenerateCodes = () => {
    const savedConsecutive = localStorage.getItem('insumedic_op_consecutive');
    const consecutive = savedConsecutive ? parseInt(savedConsecutive, 10) : 1;
    const dStr = getDisposicionDateString();
    setNumeroOrden(`OP-${dStr}-${consecutive}`);
    
    const randomSuffix = Math.floor(1000 + Math.random() * 9000);
    setLoteRP(`RP-${consecutive}`);
    setLoteFabricante(`FAB-${randomSuffix - 100}`);
    setTamanoLote(Math.floor(100 + Math.random() * 900).toString());
  };

  const applyTemplate = (tpl: typeof MED_TEMPLATES[0]) => {
    setCodigoMedicamento(tpl.codigoMedicamento);
    setNombreGenerico(tpl.nombreGenerico);
    setNombreComercial(tpl.nombreComercial);
    setConcentracion(tpl.concentracion);
    setFormaFarmaceutica(tpl.formaFarmaceutica);
    setLaboratorioFabricante(tpl.laboratorioFabricante);
    setRegistroSanitario(tpl.registroSanitario);
    setCum(tpl.cum);
    setManejoEspecial(tpl.manejoEspecial);
  };

  const isCloseToVencer = (dateStr: string): boolean => {
    if (!dateStr) return false;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return false;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const expDate = new Date(year, month, day);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = expDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays <= 180;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');
    setLastCreatedOrden(null);

    if (isReadOnly) {
      setErrorMsg('Modo Consulta Activo: Su suscripción se encuentra vencida. No se pueden registrar ni modificar órdenes.');
      return;
    }

    // Validations
    if (!numeroOrden.trim()) return setErrorMsg('Falta el: N° DE ORDEN');
    if (isNumeroOrdenDuplicado(numeroOrden)) {
      return setErrorMsg(`⚠️ EL N° DE ORDEN "${numeroOrden.trim()}" YA EXISTE EN EL SISTEMA. Por favor ingrese un número de orden único.`);
    }
    if (!loteRP.trim()) return setErrorMsg('Falta el: N° LOTE DEL RP');
    if (!loteFabricante.trim()) return setErrorMsg('Falta el: N° LOTE DEL FABRICANTE');
    if (!codigoMedicamento.trim()) return setErrorMsg('Falta el: CODIGO DE MEDICAMENTO RP');
    if (!nombreGenerico.trim()) return setErrorMsg('Falta el: NOMBRE GENÉRICO');
    if (!nombreComercial.trim()) return setErrorMsg('Falta el: NOMBRE COMERCIAL');
    if (!concentracion.trim()) return setErrorMsg('Falta la: CONCENTRACIÓN');
    if (!formaFarmaceutica.trim()) return setErrorMsg('Falta la: FORMA FARMACÉUTICA');
    if (!laboratorioFabricante.trim()) return setErrorMsg('Falta el: LABORATORIO FABRICANTE');
    if (!fechaVencimiento) return setErrorMsg('Falta la: FECHA DE VENCIMIENTO');
    // La restricción de fecha de vencimiento ha sido inhabilitada por solicitud del usuario
    // if (isCloseToVencer(fechaVencimiento)) {
    //   return setErrorMsg('MEDICAMENTO PROXIMO A VENCER, NO SACAR A PRODUCCION');
    // }
    if (!registroSanitario.trim()) return setErrorMsg('Falta el: REGISTRO SANITARIO');
    if (!cum.trim()) return setErrorMsg('Falta el: CUMS');
    if (!tamanoLote || isNaN(Number(tamanoLote)) || Number(tamanoLote) <= 0) {
      return setErrorMsg('El TAMAÑO DEL LOTE debe ser un número mayor a cero');
    }
    if (!elaboradoPor.trim()) return setErrorMsg('Falta registrar quién ha ELABORADO');

    if (isSubmitting) return;

    if (editingOrdenId) {
      if (onUpdateOrdenStatus) {
        setIsSubmitting(true);
        try {
          onUpdateOrdenStatus(editingOrdenId, estadoOrden, {
            numeroOrden: numeroOrden.trim(),
            loteRP: loteRP.trim(),
            loteFabricante: loteFabricante.trim(),
            codigoMedicamento: codigoMedicamento.trim(),
            nombreGenerico: nombreGenerico.trim(),
            nombreComercial: nombreComercial.trim(),
            concentracion: concentracion.trim(),
            formaFarmaceutica: formaFarmaceutica.trim(),
            laboratorioFabricante: laboratorioFabricante.trim(),
            fechaVencimiento,
            registroSanitario: registroSanitario.trim(),
            cum: cum.trim(),
            tamanoLote: Number(tamanoLote),
            manejoEspecial,
            elaboradoPor: elaboradoPor.trim(),
          });
          setSuccessMsg(`Orden de Producción ${numeroOrden} modificada exitosamente.`);
          handleCancelEdit();
        } finally {
          setIsSubmitting(false);
        }
      }
      return;
    }

    setIsSubmitting(true);
    try {
      // Deterministic consecutive based strictly on tenant state
      let consecutive = getNextAuthoritativeConsecutive();
      const dStr = getDisposicionDateString();
      let targetNumeroOrden = numeroOrden.trim();

      // If numeroOrden is empty or auto-generated, guarantee it strictly matches authoritative consecutive
      if (!targetNumeroOrden) {
        targetNumeroOrden = `OP-${dStr}-${consecutive}`;
      }

      // Check against any race condition with already existing orders
      while (ordenes.some(o => o.numeroOrden.trim().toLowerCase() === targetNumeroOrden.toLowerCase())) {
        consecutive += 1;
        targetNumeroOrden = `OP-${dStr}-${consecutive}`;
      }

      // Generate globally unique deterministic document ID using tenant, date, consecutive, and micro-timestamp
      const uniqueSuffix = Math.random().toString(36).substring(2, 7);
      const uniqueDocId = `ord-${Date.now()}-${consecutive}-${uniqueSuffix}`;

      const nuevaOrden: OrdenProduccion = {
        id: uniqueDocId,
        numeroOrden: targetNumeroOrden,
        loteRP: loteRP.trim() || `RP-${consecutive}`,
        loteFabricante: loteFabricante.trim(),
        codigoMedicamento: codigoMedicamento.trim(),
        nombreGenerico: nombreGenerico.trim(),
        nombreComercial: nombreComercial.trim(),
        concentracion: concentracion.trim(),
        formaFarmaceutica: formaFarmaceutica.trim(),
        laboratorioFabricante: laboratorioFabricante.trim(),
        fechaVencimiento,
        registroSanitario: registroSanitario.trim(),
        cum: cum.trim(),
        tamanoLote: Number(tamanoLote),
        manejoEspecial,
        estadoOrden,
        elaboradoPor: elaboradoPor.trim(),
        fechaRegistro: new Date().toISOString(),
        consecutivoIngreso: consecutive
      };

      onAddOrden(nuevaOrden);
      setLastCreatedOrden(nuevaOrden);
      setSuccessMsg(`Orden de Producción ${nuevaOrden.numeroOrden} agregada exitosamente.`);

      // Clear primary identifiers but keep drug templates ready
      setNumeroOrden('');
      setLoteRP('');
      setLoteFabricante('');
      setTamanoLote('');
    } catch (err: any) {
      setErrorMsg(`Error al registrar la orden: ${err?.message || err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportToExcel = () => {
    if (ordenes.length === 0) return;

    // Excel compatible CSV in Spanish format uses semicolon ';' as field separator
    // and UTF-8 BOM '\uFEFF' to preserve special characters such as 'N°', accents, etc.
    const headers = [
      'N° de Orden',
      'Lote RP',
      'Lote Fabricante',
      'Código Medicamento',
      'Nombre Genérico',
      'Nombre Comercial',
      'Concentración',
      'Forma Farmacéutica',
      'Laboratorio Fabricante',
      'Fecha Vencimiento',
      'Registro Sanitario',
      'CUMS',
      'Tamaño del Lote (Unidades)',
      'Manejo Especial',
      'Estado de la Orden',
      'Elaborado Por',
      'Verificado Por',
      'Fecha Registro'
    ];

    const rows = ordenes.map(ord => [
      ord.numeroOrden,
      ord.loteRP,
      ord.loteFabricante,
      ord.codigoMedicamento,
      ord.nombreGenerico,
      ord.nombreComercial,
      ord.concentracion,
      ord.formaFarmaceutica,
      ord.laboratorioFabricante,
      ord.fechaVencimiento,
      ord.registroSanitario,
      ord.cum,
      ord.tamanoLote,
      ord.manejoEspecial,
      ord.estadoOrden,
      ord.elaboradoPor,
      ord.verificadoPor || '',
      new Date(ord.fechaRegistro).toLocaleString('es-CO')
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(val => {
        const strVal = String(val ?? '');
        const escaped = strVal.replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `INSUMEDIC_Ordenes_Unidosis_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-8 animate-fade-in" id="registro-ordenes-container">
      
      {/* Banner / Header */}
      <div className="bg-[#071A36] border border-[#1C4F8A]/50 text-white p-6 rounded-2xl shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <h2 className="text-xl font-extrabold font-sans flex items-center text-white">
          <FileText className="h-5.5 w-5.5 mr-2.5 text-sky-300" />
          REGISTRO DE ÓRDENES DE PRODUCCIÓN
        </h2>
        <p className="text-sm text-slate-200 font-sans mt-1">
          Complete la trazabilidad y especificaciones técnicas de la preparación para fraccionamiento de unidosis.
        </p>
      </div>

      {/* Main Section Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Form Container (7/12 cols) */}
        <div className="lg:col-span-12 xl:col-span-8 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
          
          <form onSubmit={handleSubmit} className="space-y-6" id="form-registro-orden">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest font-mono">
                Formulario de Entrada (17 Campos)
              </span>
            </div>

            {isReadOnly && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-center gap-3 text-amber-900 text-xs font-sans font-bold shadow-2xs">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                <span>
                  🔒 <strong>Modo Consulta Activo:</strong> Licencia vencida. El registro y edición de órdenes de producción están deshabilitados.
                </span>
              </div>
            )}

            {errorMsg && (
              <div className="bg-red-50 text-red-700 text-xs p-3 rounded-lg border border-red-100 font-sans">
                ⚠️ {errorMsg}
              </div>
            )}

            {successMsg && (
              <div className="bg-blue-50 text-blue-900 text-xs p-3 rounded-lg border border-blue-200 font-sans flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span className="flex items-center font-medium">
                  ✓ {successMsg}
                </span>
                {lastCreatedOrden && (
                  <button
                    type="button"
                    onClick={() => handleGeneratePDF(lastCreatedOrden)}
                    className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-[#1C4F8A] hover:bg-[#071A36] text-white font-bold rounded-lg text-[11px] shadow-sm transition-all uppercase tracking-wider cursor-pointer transform hover:-translate-y-0.5 active:translate-y-0 text-center shrink-0"
                    id="btn-download-pdf-success"
                  >
                    <Download className="h-3.5 w-3.5 shrink-0" />
                    <span>Descargar PDF</span>
                  </button>
                )}
              </div>
            )}

            {/* Logical Field Groups */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              
              {/* GROUP 1: IDENTIFICACIÓN */}
              <div className="md:col-span-2">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-1 mb-3">
                  ① IDENTIFICACIÓN Y LOTE GENERAL
                </h3>
              </div>

              {/* N° DE ORDEN */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    N° DE ORDEN (OP) <span className="text-red-500">*</span>
                  </label>
                  {isNumeroOrdenDuplicado(numeroOrden) && (
                    <span className="text-[9px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded-sm animate-pulse">
                      ⚠️ DUPLICADO
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  required
                  value={numeroOrden}
                  onChange={(e) => {
                    setNumeroOrden(e.target.value);
                    if (errorMsg) setErrorMsg('');
                  }}
                  placeholder="Ej. OP-24072026-1"
                  className={`w-full h-9 px-3 text-xs bg-slate-50 border rounded-md focus:outline-hidden transition-all font-sans font-bold font-mono ${
                    isNumeroOrdenDuplicado(numeroOrden)
                      ? 'border-red-500 ring-2 ring-red-500/20 text-red-700 bg-red-50/30'
                      : 'border-slate-200 text-slate-800 focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500'
                  }`}
                  id="input-numero-orden"
                />
                {isNumeroOrdenDuplicado(numeroOrden) ? (
                  <span className="text-[10px] text-red-600 font-bold mt-1 block font-sans">
                    ⚠️ El N° de orden "{numeroOrden.trim()}" ya existe en el sistema.
                  </span>
                ) : (
                  <span className="text-[9.5px] text-slate-450 mt-1 block font-sans">
                    Identificador oficial de la Orden de Producción.
                  </span>
                )}
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Consecutivo de Ingreso <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm">
                    AUTOINCREMENTAL
                  </span>
                </div>
                <input
                  type="text"
                  required
                  readOnly
                  value={consecutivoIngreso}
                  placeholder="Consecutivo de Ingreso"
                  className="w-full h-9 px-3 text-xs bg-slate-100 border border-slate-205 rounded-md focus:outline-hidden transition-all font-sans text-slate-800 font-bold select-none cursor-not-allowed font-mono text-center"
                  id="input-consecutivo-ingreso"
                />
                <span className="text-[9.5px] text-slate-450 mt-1 block font-sans">
                  Código de control automático incremental de ingreso al sistema general.
                </span>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    N° LOTE DEL RP <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm">
                    SISTEMA AUTOMÁTICO
                  </span>
                </div>
                <input
                  type="text"
                  required
                  readOnly
                  value={loteRP}
                  placeholder="Se generará: RP-X"
                  className="w-full h-9 px-3 text-xs bg-slate-100 border border-slate-205 rounded-md focus:outline-hidden transition-all font-sans text-slate-800 font-bold select-none cursor-not-allowed font-mono text-center"
                  id="input-lote-rp"
                />
                <span className="text-[9.5px] text-slate-450 mt-1 block font-sans">
                  N° de Lote RP protocolizado: RP-Consecutivo de Ingreso.
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  N° LOTE DEL FABRICANTE <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={loteFabricante}
                  onChange={(e) => setLoteFabricante(e.target.value)}
                  placeholder="Ej. FAB-457A-12"
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-lote-fabricante"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  TAMAÑO DEL LOTE <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  required
                  value={tamanoLote}
                  onChange={(e) => setTamanoLote(e.target.value)}
                  placeholder="Ej. 350 (Unidades)"
                  min="1"
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-tamano-lote"
                />
              </div>

              {/* GROUP 2: DETALLES MEDICAMENTO */}
              <div className="md:col-span-2 mt-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-1 mb-3">
                  ② DETALLES TÉCNICOS DEL MEDICAMENTO
                </h3>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  CÓDIGO DE MEDICAMENTO RP <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={codigoMedicamento}
                  onChange={(e) => setCodigoMedicamento(e.target.value)}
                  placeholder="Ej. MED-441"
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-codigo-medicamento"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  NOMBRE GENÉRICO <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={nombreGenerico}
                  onChange={(e) => setNombreGenerico(e.target.value)}
                  placeholder="Ej. Acetaminofén"
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-nombre-generico"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  NOMBRE COMERCIAL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={nombreComercial}
                  onChange={(e) => setNombreComercial(e.target.value)}
                  placeholder="Ej. Tempra Forte"
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-nombre-comercial"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  CONCENTRACIÓN <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={concentracion}
                  onChange={(e) => setConcentracion(e.target.value)}
                  placeholder="Ej. 500 mg ó 10 mg / 2 mL"
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-concentracion"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  FORMA FARMACÉUTICA <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formaFarmaceutica}
                  onChange={(e) => setFormaFarmaceutica(e.target.value)}
                  placeholder="Ej. Tableta, Cápsula, Inyectable"
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-forma-farmaceutica"
                />
              </div>

              {/* GROUP 3: ORIGEN Y LEGAL */}
              <div className="md:col-span-2 mt-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-1 mb-3">
                  ③ REGISTRO LEGAL Y LABORATORIO
                </h3>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  LABORATORIO FABRICANTE <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={laboratorioFabricante}
                  onChange={(e) => setLaboratorioFabricante(e.target.value)}
                  placeholder="Ej. Genfar S.A."
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-laboratorio-fabricante"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  FECHA DE VENCIMIENTO <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={fechaVencimiento}
                  onChange={(e) => setFechaVencimiento(e.target.value)}
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-fecha-vencimiento"
                />
                {fechaVencimiento && isCloseToVencer(fechaVencimiento) && (
                  <motion.div 
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 p-2.5 rounded-lg bg-amber-50 border border-amber-250 text-amber-800 text-xs font-sans font-extrabold flex items-start gap-1.5 shadow-3xs"
                    id="warning-fecha-vencimiento"
                  >
                    <span className="shrink-0 text-sm">⚠️</span>
                    <span>ADVERTENCIA: MEDICAMENTO VENCIDO O PRÓXIMO A VENCER (Ingreso permitido por excepción)</span>
                  </motion.div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  REGISTRO SANITARIO <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={registroSanitario}
                  onChange={(e) => setRegistroSanitario(e.target.value)}
                  placeholder="Ej. INVIMA 2019M-001223"
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-registro-sanitario"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  CUMS <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={cum}
                  onChange={(e) => setCum(e.target.value)}
                  placeholder="Ej. 19951234-5"
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="input-cum"
                />
              </div>

              {/* GROUP 4: CLASIFICACIÓN Y FIRMAS */}
              <div className="md:col-span-2 mt-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-1 mb-3">
                  ④ RESTRICCIÓN Y FIRMAS DE RESPONSABILIDAD
                </h3>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  MEDICAMENTO DE MANEJO ESPECIAL <span className="text-red-500">*</span>
                </label>
                <select
                  value={manejoEspecial}
                  onChange={(e) => setManejoEspecial(e.target.value as 'SI' | 'NO' | 'FOTOSENSIBLE')}
                  className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800"
                  id="select-manejo-especial"
                >
                  <option value="NO">NO</option>
                  <option value="SI">SI (Control Especial / Monopolio del Estado)</option>
                  <option value="FOTOSENSIBLE">Medicamento Fotosensible</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  ESTADO DE LA ORDEN <span className="text-red-500">*</span>
                </label>
                {editingOrdenId ? (
                  <select
                    value={estadoOrden}
                    onChange={(e) => setEstadoOrden(e.target.value as any)}
                    className="w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500/10 focus:ring-emerald-500 transition-all font-sans text-slate-800 font-bold"
                    id="select-estado-orden"
                  >
                    <option value="Pendiente por Aceptar">Pendiente por Aceptar</option>
                    <option value="Aceptada">Aceptada (En Proceso)</option>
                    <option value="Cerrada">Cerrada</option>
                    <option value="Cancelada">Cancelada</option>
                  </select>
                ) : (
                  <div className="w-full h-9 px-3 bg-slate-100 border border-slate-200 rounded-md flex items-center text-xs font-bold text-slate-500 font-sans select-none truncate">
                    Pendiente por Aceptar
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 text-emerald-800">
                  ⚡ ELABORADO POR <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  readOnly
                  value={elaboradoPor}
                  title="Este campo se registra automáticamente con el usuario en sesión activa"
                  className="w-full h-9 px-3 text-xs bg-slate-100 border border-slate-200 rounded-md focus:outline-hidden transition-all font-sans text-slate-600 font-bold select-none cursor-not-allowed"
                  id="input-elaborado-por"
                />
              </div>

            </div>

            {editingOrdenId ? (
              <div className="flex gap-4 mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="grow h-10 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-sans text-xs font-bold uppercase tracking-wider rounded-lg transition-all shadow-sm cursor-pointer flex items-center justify-center space-x-2"
                  id="btn-edit-production-order-save"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>{isSubmitting ? 'Guardando...' : 'Guardar Cambios de la Orden'}</span>
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleCancelEdit}
                  className="px-4 h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 font-sans text-xs font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center disabled:opacity-50"
                  id="btn-edit-production-order-cancel"
                >
                  Cancelar Edición
                </button>
              </div>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-10 bg-[#1C4F8A] hover:bg-[#071A36] active:bg-[#0D274F] disabled:opacity-50 disabled:cursor-not-allowed text-white font-sans text-xs font-bold uppercase tracking-wider rounded-lg transition-all shadow-sm cursor-pointer flex items-center justify-center space-x-2 mt-6"
                id="btn-add-production-order"
              >
                <Plus className="h-4 w-4" />
                <span>{isSubmitting ? 'Registrando Orden...' : 'Registrar e Iniciar Orden de Producción'}</span>
              </button>
            )}
          </form>
        </div>

        {/* Info panel / instructions (5/12 cols) */}
        <div className="lg:col-span-12 xl:col-span-4 space-y-6">
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center mb-4">
              <Shield className="h-4.5 w-4.5 text-[#1C4F8A] mr-2" />
              Garantía de Calidad INSUMEDIC
            </h3>
            <ul className="space-y-3.5 text-xs text-slate-600 font-sans">
              <li className="flex items-start">
                <span className="h-4.5 w-4.5 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center font-bold text-[10px] mr-2.5 shrink-0 mt-0.5">1</span>
                <span>Al registrar un producto con <strong>Manejo Especial</strong> como "SI", el lote será auditado bajo precintos de seguridad adicionales y supervisado por dirección técnica.</span>
              </li>
              <li className="flex items-start">
                <span className="h-4.5 w-4.5 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center font-bold text-[10px] mr-2.5 shrink-0 mt-0.5">2</span>
                <span>Las <strong>Órdenes Aceptadas</strong> pueden disponer productos selectivamente en el siguiente menú para su aprobación física o rechazo en cuarentena.</span>
              </li>
              <li className="flex items-start">
                <span className="h-4.5 w-4.5 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center font-bold text-[10px] mr-2.5 shrink-0 mt-0.5">3</span>
                <span>El número concatenado <strong>CUMS</strong> y <strong>Registro Sanitario INVIMA</strong> son obligatorios para posibilitar auditorías del Ministerio de Salud.</span>
              </li>
            </ul>
          </div>
        </div>

      </div>

      {/* LOWER TABular / product lists Section conforming to requirement 3 */}
      {/* "Adicional a esto en la parte inferior debe aparecer la información cada vez que yo agregue un nuevo producto y la opción de eliminar." */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200/90 shadow-sm" id="seccion-historico-productos">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-100 pb-4 mb-4 gap-2">
          <div>
            <h3 className="font-sans font-extrabold text-slate-900 text-base">
              Órdenes & Productos Registrados
            </h3>
            <p className="text-xs text-slate-500 font-sans">
              Lista de control de lotes activos. Se muestra la información ingresada cada vez que se agrega un producto, con opción de eliminar administrativamente.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 self-stretch sm:self-auto justify-end">
            {ordenes.length > 0 && (
              <button
                onClick={handleExportToExcel}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#1C4F8A] hover:bg-[#071A36] text-white font-sans text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-sm hover:shadow-md"
                title="Exportar órdenes registradas a Excel CSV"
                id="btn-export-ordenes-excel"
              >
                <FileSpreadsheet className="h-4 w-4" />
                <span>Exportar a Excel</span>
              </button>
            )}
             {selectedOrdenIds.length > 0 && currentUser.cargo === 'Director Técnico y de Calidad' && (
               <button
                 onClick={() => setShowDeleteSelectedConfirm(true)}
                 className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-rose-500 hover:bg-rose-600 text-white font-sans text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-sm hover:shadow-md animate-fade-in animate-duration-200"
                 title="Eliminar las órdenes seleccionadas"
                 id="btn-delete-selected-records"
               >
                 <Trash2 className="h-4 w-4 text-rose-100" />
                 <span>Borrar Seleccionados ({selectedOrdenIds.length})</span>
               </button>
             )}
             {currentUser.cargo === 'Director Técnico y de Calidad' && ordenes.length > 0 && (
               <button
                 onClick={() => setShowClearAllConfirm(true)}
                 className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-sans text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-sm hover:shadow-md"
                 title="Borrar todas las órdenes creadas y disposiciones de producto"
                 id="btn-clear-all-data-records"
               >
                 <Trash2 className="h-4 w-4" />
                 <span>Borrar Todo</span>
               </button>
             )}
            <span className="px-2.5 py-1 bg-slate-100 text-slate-705 font-mono text-[10px] rounded-full font-bold">
              Total Lotes: {ordenes.length}
            </span>
          </div>
        </div>

        {/* Quick Search Bar for Órdenes */}
        {ordenes.length > 0 && (
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
              <Search className="h-4 w-4" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscador rápido de Órdenes: Ingrese N° de Orden (OP), Lote RP, Medicamento, Código o Elaborador..."
              className="w-full pl-9 pr-8 py-2 text-xs bg-slate-50 border border-slate-200/90 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-sans text-slate-800 placeholder-slate-400 shadow-3xs"
              id="input-search-ordenes"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                title="Limpiar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {ordenes.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-xs font-sans">
            No se han registrado productos u órdenes de producción en esta sesión. Use el formulario superior para registrar uno.
          </div>
        ) : filteredOrdenes.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-xs font-sans bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
            No se encontraron órdenes de producción que coincidan con "<strong className="font-semibold text-slate-700">{searchTerm}</strong>".
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse" id="tabla-productos-ordenes">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center w-12 select-none">
                    <input 
                      type="checkbox" 
                      className="cursor-pointer h-4 w-4 rounded-md border-slate-350 text-teal-600 focus:ring-teal-500 transition-all focus:ring-offset-0 bg-white"
                      checked={filteredOrdenes.length > 0 && filteredOrdenes.every(o => selectedOrdenIds.includes(o.id))}
                      onChange={() => {
                        const allFilteredSelected = filteredOrdenes.every(o => selectedOrdenIds.includes(o.id));
                        if (allFilteredSelected) {
                          setSelectedOrdenIds(prev => prev.filter(id => !filteredOrdenes.some(o => o.id === id)));
                        } else {
                          setSelectedOrdenIds(prev => Array.from(new Set([...prev, ...filteredOrdenes.map(o => o.id)])));
                        }
                      }}
                    />
                  </th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Consecutivo de Ingreso</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Medicamento (Cód.)</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Lote RP / Fabr</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Fabricante / Vence</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-right font-sans">Tamaño Lote</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center font-sans">Especial</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center font-sans">Estado</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Elaborado/Verificado</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center font-sans">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredOrdenes.map((ord) => (
                  <tr key={ord.id} className={`hover:bg-slate-50/80 transition-colors ${selectedOrdenIds.includes(ord.id) ? 'bg-teal-50/20 hover:bg-teal-50/30' : ''}`} id={`row-orden-${ord.id}`}>
                    {/* Checkbox */}
                    <td className="p-3 text-center">
                      <input 
                        type="checkbox"
                        className="cursor-pointer h-4 w-4 rounded-md border-slate-350 text-teal-600 focus:ring-teal-500 transition-all focus:ring-offset-0 bg-white"
                        checked={selectedOrdenIds.includes(ord.id)}
                        onChange={() => handleToggleSelect(ord.id)}
                      />
                    </td>
                    
                    {/* Consecutivo de Ingreso */}
                    <td className="p-3 text-xs font-mono font-bold text-center text-slate-800">
                      {ord.consecutivoIngreso !== undefined ? ord.consecutivoIngreso : (ord.numeroOrden.split('-').pop() || '1')}
                    </td>
                    
                    {/* Medication */}
                    <td className="p-3 text-xs font-sans">
                      <div className="font-bold text-slate-900">{ord.nombreGenerico}</div>
                      <div className="text-[11px] text-slate-500">{ord.nombreComercial} ({ord.concentracion})</div>
                      <div className="text-[10px] font-mono text-slate-400">{ord.formaFarmaceutica} • Cod: {ord.codigoMedicamento}</div>
                    </td>
                    
                    {/* Lot codes */}
                    <td className="p-3 text-xs font-sans">
                      <div className="font-mono text-[11px]"><strong>LRP:</strong> {ord.loteRP}</div>
                      <div className="font-mono text-[11px] text-slate-500"><strong>FAB:</strong> {ord.loteFabricante}</div>
                    </td>
                    
                    {/* Manufacturer & exp */}
                    <td className="p-3 text-xs font-sans">
                      <div className="text-slate-800 truncate max-w-[140px]">{ord.laboratorioFabricante}</div>
                      <div className="text-[11px] text-slate-500 flex items-center mt-1">
                        <Calendar className="h-3 w-3 mr-1 text-slate-400" />
                        {ord.fechaVencimiento}
                      </div>
                    </td>

                    {/* Lot size */}
                    <td className="p-3 text-xs font-mono font-semibold text-right text-slate-800">
                      {ord.tamanoLote.toLocaleString()} ud.
                    </td>

                    {/* Special status icon */}
                    <td className="p-3 text-xs text-center">
                      {ord.manejoEspecial === 'SI' ? (
                        <span className="inline-flex px-1.5 py-0.5 rounded-sm bg-purple-50 text-purple-700 font-bold text-[10px] border border-purple-100">
                          SÍ
                        </span>
                      ) : ord.manejoEspecial === 'FOTOSENSIBLE' ? (
                        <span className="inline-flex px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 font-bold text-[10px] border border-amber-100 uppercase">
                          SI (FOTOSENSIBLE)
                        </span>
                      ) : (
                        <span className="inline-flex px-1.5 py-0.5 rounded-sm bg-slate-50 text-slate-400 font-sans text-[10px]">
                          No
                        </span>
                      )}
                    </td>

                     {/* Status dropdown/icon */}
                    <td className="p-3 text-xs text-center font-sans">
                      <span className={`inline-flex px-2 py-0.5 rounded-full font-semibold text-[10px] ${
                        ord.estadoOrden === 'Pendiente por Aceptar'
                          ? 'bg-slate-100 text-slate-600 border border-slate-200'
                          : ord.estadoOrden === 'Cancelada'
                          ? 'bg-rose-50 text-rose-850 border border-rose-100'
                          : ord.estadoOrden === 'Aceptada' 
                          ? 'bg-emerald-50 text-emerald-800 border border-emerald-100' 
                          : 'bg-teal-50 text-teal-800 border border-teal-100'
                      }`}>
                        {ord.estadoOrden || 'Pendiente por Aceptar'}
                      </span>
                    </td>

                    {/* Elaborado / Verificado */}
                    <td className="p-3 text-xs font-sans">
                      <div className="text-[11px] text-slate-700"><strong>Elab:</strong> {ord.elaboradoPor}</div>
                      {ord.verificadoPor && (
                        <div className="text-[11px] text-slate-500"><strong>Verif:</strong> {ord.verificadoPor}</div>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="p-3 text-xs text-center flex items-center justify-center space-x-2">
                      <button
                        onClick={() => handleGeneratePDF(ord)}
                        className="p-1 px-2 hover:bg-teal-50 text-teal-600 hover:text-teal-700 rounded-md transition-colors border border-transparent hover:border-teal-100 cursor-pointer"
                        title="Descargar Orden en PDF"
                        id={`btn-pdf-orden-${ord.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      {(ord.estadoOrden !== 'Cerrada' || currentUser.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser.cargo === 'Director Técnico y de Calidad') && (
                        <button
                          onClick={() => handleStartEdit(ord)}
                          className="p-1 px-2 hover:bg-amber-50 text-amber-600 hover:text-amber-700 rounded-md transition-colors border border-transparent hover:border-amber-100 cursor-pointer"
                          title="Editar Orden de Producción"
                          id={`btn-edit-orden-${ord.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {currentUser.cargo === 'Director Técnico y de Calidad' && (
                        <button
                          onClick={() => setPendingDeleteId(ord.id)}
                          className="p-1 px-2 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-md transition-colors border border-transparent hover:border-red-100 cursor-pointer"
                          title="Eliminar registro"
                          id={`btn-delete-orden-${ord.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ========================================================= */}
      {/* CUSTOM DIALOG MODALS BYPASSING BLOCKED IFRAME POPUPS */}
      {/* ========================================================= */}
      
      {/* 1. Modal confirming single record delete */}
      {pendingDeleteId && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in animate-duration-150" id="modal-delete-single">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xl p-6 max-w-md w-full space-y-4">
            <h3 className="font-sans font-extrabold text-slate-950 text-base flex items-center gap-2">
              <span className="p-1.5 bg-amber-50 text-amber-600 rounded-lg">⚠️</span>
              ¿Confirmar Eliminación?
            </h3>
            <p className="text-sm text-slate-600 font-sans leading-relaxed">
              Está a punto de eliminar el registro de producción y cualquier producto o disposición asociada a la orden <strong className="font-bold text-slate-850">
                {ordenes.find(o => o.id === pendingDeleteId)?.numeroOrden || ''}
              </strong>. Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setPendingDeleteId(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-705 rounded-xl font-bold text-xs font-sans transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (pendingDeleteId) {
                    onDeleteOrden(pendingDeleteId);
                    setSelectedOrdenIds(prev => prev.filter(id => id !== pendingDeleteId));
                    setPendingDeleteId(null);
                  }
                }}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs font-sans transition-colors cursor-pointer shadow-md hover:shadow-lg"
              >
                Eliminar Registro
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Modal confirming selective bulk delete */}
      {showDeleteSelectedConfirm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in animate-duration-150" id="modal-delete-selected">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xl p-6 max-w-md w-full space-y-4">
            <h3 className="font-sans font-extrabold text-slate-950 text-base flex items-center gap-2">
              <span className="p-1.5 bg-rose-50 text-rose-600 rounded-lg">🛑</span>
              ¿Eliminar Órdenes Seleccionadas?
            </h3>
            <p className="text-sm text-slate-600 font-sans leading-relaxed">
              ¿Está seguro de que desea eliminar permanentemente las <span className="font-bold text-slate-900">{selectedOrdenIds.length}</span> órdenes seleccionadas y todas sus disposiciones asociadas?
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowDeleteSelectedConfirm(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-705 rounded-xl font-bold text-xs font-sans transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (onDeleteMultipleOrdenes) {
                    onDeleteMultipleOrdenes(selectedOrdenIds);
                  } else {
                    selectedOrdenIds.forEach(id => onDeleteOrden(id));
                  }
                  setSelectedOrdenIds([]);
                  setShowDeleteSelectedConfirm(false);
                }}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs font-sans transition-colors cursor-pointer shadow-md hover:shadow-lg font-bold"
              >
                Sí, Eliminar {selectedOrdenIds.length} Lotes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. Modal confirming empty/clear all database */}
      {showClearAllConfirm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in animate-duration-150" id="modal-clear-all-data">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xl p-6 max-w-md w-full space-y-4">
            <h3 className="font-sans font-extrabold text-slate-950 text-base flex items-center gap-2">
              <span className="p-1.5 bg-red-50 text-red-600 rounded-lg">⛔</span>
              ¿Borrar Todo Permanentemente?
            </h3>
            <p className="text-sm text-slate-600 font-sans leading-relaxed">
              Esta acción eliminará de forma irreversible <strong className="font-bold text-slate-900">absolutamente todas</strong> las órdenes de producción registradas y las disposiciones tramitadas hasta el momento.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowClearAllConfirm(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-705 rounded-xl font-bold text-xs font-sans transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (onClearAllData) onClearAllData();
                  setSelectedOrdenIds([]);
                  setShowClearAllConfirm(false);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-xs font-sans transition-colors cursor-pointer shadow-md hover:shadow-lg"
              >
                Sí, Borrar Todo
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
